/* eslint-disable max-len */
import { supportedLanguageCodes } from '@helpers/languages';
import { CONSTANTS } from '@factories/constants';
import oktaConfig from '@oktaConfig/oktaConfig';
import getBaseUrl from './getBaseUrl';

export const getUpdateInformationCTAURL = (encodedDunsNumber: string, updatedInfoCTAURL: string): string => {
    const url = document.location.host;
    let envPrefix = '.dnb';
    let updateEnvPrefix = 'www';
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1) {
        envPrefix = '-qa.malibucoding';
        updateEnvPrefix = 'dev';
    }
    else if (url.indexOf('qa') > -1) {
        envPrefix = '-qa.malibucoding';
        updateEnvPrefix = 'qa';
    }
    else if (url.indexOf('stg') > -1) {
        envPrefix = '-stg.malibucoding';
        updateEnvPrefix = 'stage';
    }
    return updatedInfoCTAURL.replace('{envPrefix}', envPrefix).replace('{encodedDunsNumber}', encodedDunsNumber)
        .replace('{updateEnvPrefix}', updateEnvPrefix);
};

export const getViewEditSearchUrl = (addCompanyCTAURL: string): string => {
    const url = document.location.host;
    let envPrefix = 'www';
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1) {
        envPrefix = 'www-devaka';
    }
    else if (url.indexOf('qa') > -1) {
        envPrefix = 'www-qaaka';
    }
    else if (url.indexOf('stg') > -1) {
        envPrefix = 'www-stgaka';
    }
    return addCompanyCTAURL.replace('{envPrefix}', envPrefix);
};

export const getExchangeBetaUrl = (marketPlaceSectionURL: string): string => {
    const url = document.location.host;
    let envPrefix = 'www';
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1) {
        envPrefix = 'www-devaka';
    }
    else if (url.indexOf('qa') > -1) {
        envPrefix = 'www-qaaka';
    }
    else if (url.indexOf('stg') > -1) {
        envPrefix = 'www-stgaka';
    }
    return marketPlaceSectionURL.replace('{envPrefix}', envPrefix);
};

export const getOktaIssuerUrl = (): void => {
    const url = document.location.host;
    let issuer = CONSTANTS.OKTA_ISSUER_PROD;
    let baseUrl = CONSTANTS.OKTA_BASE_URL_PROD;
    let id = CONSTANTS.OKTA_ID_PROD;
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1 || url.indexOf('qa') > -1) {
        issuer = CONSTANTS.OKTA_ISSUER_QA;
        baseUrl = CONSTANTS.OKTA_BASE_URL_QA;
        id = CONSTANTS.OKTA_ID_QA;
        if (url.indexOf('localhost') > -1) {
            id = CONSTANTS.OKTA_ID_DEV;
        }
    }
    else if (url.indexOf('stg') > -1) {
        issuer = CONSTANTS.OKTA_ISSUER_STG;
        baseUrl = CONSTANTS.OKTA_BASE_URL_STG;
        id = CONSTANTS.OKTA_ID_STG;
    }
    else {
        issuer = CONSTANTS.OKTA_ISSUER_PROD;
        baseUrl = CONSTANTS.OKTA_BASE_URL_PROD;
        id = CONSTANTS.OKTA_ID_PROD;
    }
    oktaConfig.config.baseUrl = baseUrl;
    oktaConfig.oidc.issuer = issuer;
    oktaConfig.config.authParams.issuer = issuer;
    [oktaConfig.oidc.clientId, oktaConfig.config.clientId, oktaConfig.config.authParams.clientId] = Array(3).fill(id);
};

/**
 * Getting the Small Business url based on the application environment(DEV,QA,STG & PROD)
 * @returns Small Business URL
 */
export const getSmallBusinessUrl = (): string => {
    const url = document.location.host;
    let smallBusinessUrl = 'https://www.dnb.com/products/small-business.html';// Default Prod URL
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1) {
        smallBusinessUrl = 'https://dev.dnb.com/products/small-business.html';
    }
    else if (url.indexOf('qa') > -1) {
        smallBusinessUrl = 'https://qa.dnb.com/products/small-business.html';
    }
    else if (url.indexOf('stg') > -1) {
        smallBusinessUrl = 'https://stage.dnb.com/products/small-business.html';
    }
    return smallBusinessUrl;
};

/**
 * Return Cancel_renewal_URL based on the Host URL(DEV,QA/STG and PROD) Environment
 */
export const getCancelRenewalURL = (cancelRenewalURL: string): string => {
    const url = document.location.host;
    let envPrefix = 'service';
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1 || url.indexOf('qa') > -1) {
        envPrefix = 'dev-service'; // There is no QA environment for the Service Portal
    }
    else if (url.indexOf('stg') > -1) {
        envPrefix = 'uat-service';
    }

    return cancelRenewalURL.replace('{envPrefix}', envPrefix);
};

// Get Magento Url for My Order
export const magentoUrl = (language_code: string): string => {
    if (supportedLanguageCodes.includes(language_code) && language_code !== 'en-us') {
        let revisedLanguageCode = language_code;
        if (language_code.toLowerCase() === 'en-gb') {
            revisedLanguageCode = 'uk';
        }
        else if (language_code.toLowerCase() === 'en-ie') {
            revisedLanguageCode = 'ie';
        }
        return `https://${magentoEnvironment()}.dnb.com/${revisedLanguageCode}/sales/order/history/`;
    }

    return `https://${magentoEnvironment()}.dnb.com/sales/order/history/`;
};

// Get Magento Url for My Subscriptions
export const subscriptionMagentoUrl = (language_code: string): string => {
    let revisedLanguageCode = language_code;
    if (supportedLanguageCodes.includes(language_code) && language_code !== 'en-us') {
        if (language_code.toLowerCase() === 'en-gb') {
            revisedLanguageCode = 'uk';
        }
        else if (language_code.toLowerCase() === 'en-ie') {
            revisedLanguageCode = 'ie';
        }
        return `https://${magentoEnvironment()}.dnb.com/${revisedLanguageCode}/aw_sarp2/profile/`;
    }

    return `https://${magentoEnvironment()}.dnb.com/aw_sarp2/profile/`;
};

// Gets magento env url
export const magentoEnvironment = (): string => {
    let environment = '';
    if (window.location.host === 'my.dnb.com') {
        environment = 'buy';
    }
    else if (window.location.host.indexOf('qa') > -1 || window.location.host.indexOf('dev') > -1) {
        environment = 'buy-qa';
    }
    else if (window.location.host.indexOf('stg') > -1) {
        environment = 'buy-stg';
    }
    else environment = 'buy-qa';
    return environment;
};

export const getExploreDnBTileURL = (exploreDNBTileURL: string): string => {
    const url = document.location.host;
    let envPrefix = 'www';
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1) {
        envPrefix = 'www-devaka';
    }
    else if (url.indexOf('qa') > -1) {
        envPrefix = 'www-qaaka';
    }
    else if (url.indexOf('stg') > -1) {
        envPrefix = 'www-stgaka';
    }
    return exploreDNBTileURL.replace('{envPrefix}', envPrefix);
};

export const getDUNSProductSelectionUrl = (flow: string): string => {
    const url = getBaseUrl();
    const flowParam = flow === 'CI' ? '&flow=CI' : '';
    let dunsProductSelectionUrl = `https://www.dnb.com/duns-number/get-a-dnb-duns-number.html?step=product${flowParam}`;// Default Prod URL
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1 || url.indexOf('qa') > -1) {
        dunsProductSelectionUrl = `https://qa.dnb.com/duns-number/get-a-dnb-duns-number.html?step=product${flowParam}`;
    }
    else if (url.indexOf('stg') > -1) {
        dunsProductSelectionUrl = `https://stage.dnb.com/duns-number/get-a-dnb-duns-number.html?step=product${flowParam}`;
    }
    return dunsProductSelectionUrl;
};
/**
 * @returns URL for dnb.com
 */
export const getDnBDotComURL = (): string => {
    const url = document.location.host;
    const DnBDotComURL = 'https://{envPrefix}.dnb.com/';
    let envPrefix = 'www';
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1) {
        envPrefix = 'www-devaka';
    }
    else if (url.indexOf('qa') > -1) {
        envPrefix = 'www-qaaka';
    }
    else if (url.indexOf('stg') > -1) {
        envPrefix = 'www-stgaka';
    }
    return DnBDotComURL.replace('{envPrefix}', envPrefix);
};

export const getDUNsUrl = (countryCode: string): string => {
    const url = document.location.host;
    let dunsProductSelectionUrl = 'https://www.dnb.com/duns/get-a-dnb-duns-number.html#search';// Default Prod URL
    let envPrefix = 'www.dnb.com';
    if (countryCode === 'CA') {
        dunsProductSelectionUrl = 'https://www.dnb.com/ca-en/duns-number/get-a-duns.html';
    }
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1 || url.indexOf('qa') > -1) {
        envPrefix = 'qa.dnb.com';
        if (countryCode === 'CA') {
            envPrefix = 'www-qaaka.dnb.com';
        }
    }
    else if (url.indexOf('stg') > -1) {
        envPrefix = 'stage.dnb.com';
        if (countryCode === 'CA') {
            envPrefix = 'www-stgaka.dnb.com';
        }
    }
    dunsProductSelectionUrl = dunsProductSelectionUrl.replace('www.dnb.com', envPrefix);
    return dunsProductSelectionUrl;
};

// Business directory url
export const getbusinessDirectoryUrl = (): string => {
    const url = document.location.host;
    let businessDirectoryUrl = 'https://www.dnb.com/business-directory.html';// Default Prod URL
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1 || url.indexOf('qa') > -1) {
        businessDirectoryUrl = 'https://qa.dnb.com/business-directory.html';
    }
    else if (url.indexOf('stg') > -1) {
        businessDirectoryUrl = 'https://stage.dnb.com/business-directory.html';
    }
    return businessDirectoryUrl;
};

// Validate gated content
export const getGatedContentUrl = (redirectUrl: string): { redirectUrl: string, gatedContentFlag: boolean } => {
    const url = document.location.host;
    let gatedContentUrl = 'https://www.dnb.com/business-directory/company-profiles.';// Default Prod URL
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1) {
        gatedContentUrl = 'https://dev.dnb.com/business-directory/company-profiles.';
    }
    else if (url.indexOf('qa') > -1) {
        gatedContentUrl = 'https://qa.dnb.com/business-directory/company-profiles.';
    }
    else if (url.indexOf('stg') > -1) {
        gatedContentUrl = 'https://stage.dnb.com/business-directory/company-profiles.';
    }
    const gatedContentFlag = redirectUrl.includes(gatedContentUrl);
    return { redirectUrl, gatedContentFlag };
};

// Get getDunsRegisteredLogoUrl
export const getDunsRegisteredLogoUrl = (): string => 'https://www.dnb.com/content/dam/english/image-library/Modernization/logos/duns_registered_logo.png';

// Get getDRSpdfInstructionsUrl
export const getDRSpdfInstructionsUrl = (): string => 'https://www.dnb.com/content/dam/english/dnb-solutions/DRS_Seal_Installation_Guide.pdf';

// Security Valunaribility check (need to whitelist the url for the redirection check)
export const checkValidDomainRedirectURLs = (url: string): string => {
    const validDomainList: string[] = ['https://www.dnb.com', 'https://stage.dnb.com', 'https://qa.dnb.com', 'https://dev.dnb.com',
        'https://www-stgaka.dnb.com', 'https://www-qaaka.dnb.com', 'https://www-devaka.dnb.com',
        'https://buy.dnb.com', 'https://buy-stg.dnb.com', 'https://buy-qa.dnb.com',
        'https://service.dnb.com', 'https://learning.dnb.com',
        'https://dashboard.dnb.com', 'https://dashboard-qa.malibucoding.com', 'https://dashboard-stg.malibucoding.com',
        'https://www.dnb.co.uk', 'https://learning.dnb.co.uk', 'https://pl.bisnode.pl', 'https://qa.mydnb.nonprod.gcpdnb.net', 'https://my-stg.dnb.com',
        'https://qa.smb.nonprod.gcpdnb.net', 'https://stg.smb.prod.gcpdnb.net', 'https://prod.smb.prod.gcpdnb.net'];
    if (validDomainList.includes(new URL(url).origin)) { // check origin of the the redirect URL with whitelisted domains
        return url;
    }
    return 'https://my.dnb.com/';
};

// CIUKIFree  url
export const getCIUkiFreeUrl = (countryCode: string): string => {
    const url = document.location.host;
    if (countryCode === 'GB') {
        let creditInsightsUrlUK = 'https://www.dnb.co.uk/products/dnb-credit-insights.html';// Default Prod URL
        if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1 || url.indexOf('qa') > -1) {
            creditInsightsUrlUK = 'https://qa.dnb.co.uk/products/dnb-credit-insights.html';
        }
        else if (url.indexOf('stg') > -1) {
            creditInsightsUrlUK = 'https://stage.dnb.co.uk/products/dnb-credit-insights.html';
        }
        return creditInsightsUrlUK;
    }
    let creditInsightsUrlIE = 'https://www.dnb.com/ie/products/dnb-credit-insights.html';// Default Prod URL
    if (url.indexOf('localhost') > -1 || url.indexOf('dev') > -1 || url.indexOf('qa') > -1) {
        creditInsightsUrlIE = 'https://qa.dnb.com/ie/products/dnb-credit-insights.html';
    }
    else if (url.indexOf('stg') > -1) {
        creditInsightsUrlIE = 'https://stage.dnb.com/ie/products/dnb-credit-insights.html';
    }
    return creditInsightsUrlIE;
};

export const getSignUpForCreditMonitorUrl = (): string => {
    const url = document.location.host;
    let dunsProductSelectionUrl = 'https://www.dnb.co.uk/products/finance-credit-risk/dnb-credit-monitor.html?utm_medium=in_product&utm_campaign=upsell_creditmonitor&utm_content=cpl_cm_preview';// Default Prod URL

    if (url.includes('localhost') || url.includes('dev') || url.includes('qa')) {
        dunsProductSelectionUrl = 'https://qa.dnb.co.uk/products/finance-credit-risk/dnb-credit-monitor.html?utm_medium=in_product&utm_campaign=upsell_creditmonitor&utm_content=cpl_cm_preview';
    }
    else if (url.includes('stg')) {
        dunsProductSelectionUrl = 'https://stage.dnb.co.uk/products/finance-credit-risk/dnb-credit-monitor.html?utm_medium=in_product&utm_campaign=upsell_creditmonitor&utm_content=cpl_cm_preview';
    }
    return dunsProductSelectionUrl;
};

// Construct Okta deep link url
export const getConstructDeepLinkUrl = (appName: string, appId: string): string => {
    const url = getBaseUrl();
    let OKTA_SAML_DEEP_LINK = `https://sso.dnb.com/app/${appName}/${appId}/sso/saml`;
    if (url.includes('localhost') || url.includes('dev') || url.includes('qa')) {
        OKTA_SAML_DEEP_LINK = `https://qa.sso.dnb.com/app/${appName}/${appId}/sso/saml`;
    }
    else if (url.includes('stg')) {
        OKTA_SAML_DEEP_LINK = `https://stg.sso.dnb.com/app/${appName}/${appId}/sso/saml`;
    }
    return OKTA_SAML_DEEP_LINK;
};

export const getDfcOktaUrl = (caseId: string): string => {
    const url = getBaseUrl();
    let appName = 'dnb_dfc_1';
    let appId = 'exk9lchshtT3eAOva5d7';

    if (url.includes('dev')) {
        appName = 'dnb-qa_dfcdevgcp_1';
        appId = 'exkfdbldgyzlSDKCO1d7';
    }
    else if (url.includes('qa') || url.includes('localhost')) {
        appName = 'dnb-qa_dfcqagcp_2';
        appId = 'exkfrsfsp44fyuiSc1d7';
    }
    else if (url.includes('stg')) {
        appName = 'dnb-stg_dfcstage_1';
        appId = 'exk81qlw5nVPMLwgG1d7';
    }
    const query = `?cid=cid=${caseId}&case_type=FREE&flow=GAD`;
    const encodedUrl = encodeURIComponent(btoa(query));
    return `${getConstructDeepLinkUrl(appName, appId)}?RelayState=${encodedUrl}`;
};

export const performSafeDomainRedirect = (url: string): void => {
    const windowLocationObj: Window = window;
    try {
        //  Need to check the list of redirect first
        // const safeUrl = checkValidDomainRedirectURLs(url);
        windowLocationObj.location = url;

    } catch (e) {
        console.error(`Invalid URL provided for redirection: "${url}"`, e);
    }
};
